// Risk register seed — [syn] screenshot-grade rows (A20 komatsu-run, a20-risk).
// Nine RiskEntry rows re-read from four FMEA circuit sheets (Crushing, Ball
// Mill, Zn Flotation, Thickeners); the nine SafetyInterlock rows transcribe
// v19 M6 §4's table verbatim; the ControlVerificationCheck rows are M10 §7's
// proactive-verification layer over that interlock register. RK-02's FMEA
// severity, occurrence, detection and RPN read from fm-trunnion; every other
// S/O/D/RPN combination is a [syn] re-score in the same band as sibling rows
// in bunker-hill-advisory-seed.ts (fm-apex, fm-air-flow).
// controlMeasure / reviewCadence / failureModeId and SHIFT_CHECKS are [syn]
// glass-run a20-risk augmentations — only RK-02 and RK-07 map a failure mode.

import type { Persona } from '@/shared/types';
import { FAILURE_MODES } from '@/versions/v1/shared/bunker-hill-advisory-seed';
import { BM01_BEARING_STOP_HOURS } from '@/versions/v1/shared/bunker-hill-bearing-stop';
import { READINGS } from '@/versions/v1/shared/bunker-hill-readings';

const BM01_FAILURE_MODE_ID = 'fm-trunnion';

function failureModeScore(failureModeId: string) {
  const row = FAILURE_MODES.find((candidate) => candidate.id === failureModeId);
  if (!row) throw new Error(`Risk seed: missing FMEA row ${failureModeId}`);
  return { severity: row.s, occurrence: row.o, detection: row.d, rpn: row.rpn };
}

export type OverrideAuthority = 'No one' | 'Supervisor only' | 'Maintenance mode' | 'Operator';

export type RiskReviewCadence = 'Each shift' | 'Daily' | 'Weekly' | 'Monthly';

export type RiskEntry = {
  id: string;
  label: string;
  circuit: string;
  subprocessId: string;
  cause: string;
  effect: string;
  safetySeverity: number;
  productionSeverity: number;
  /** Original blended FMEA severity — kept alongside the two-axis re-score (v19 M6 §2). */
  severity: number;
  occurrence: number;
  detection: number;
  rpn: number;
  ownerTeam: string;
  reviewDate: string;
  /** Hours the trip/event is assumed to run before recovery. */
  tripHours: number;
  /** Failure-repair hours are an unbuffered plant stop; other trips respect downstream buffers. */
  hoursSource?: 'failure-repair';
  plannedStopHours?: number;
  /** Hours of downstream buffer that absorb the event before Budget throughput is lost. */
  bufferHours: number;
  linkedWorkOrder?: string;
  controlMeasure: string;
  reviewCadence: RiskReviewCadence;
  failureModeId?: string;
  provenance: string;
};

export type ShiftCheckKind = 'field' | 'reagent' | 'loop' | 'equipment';

export type ShiftCheck = {
  id: string;
  title: string;
  description: string;
  frequency: 'Each shift';
  riskLevel: 'high' | 'medium';
  kind: ShiftCheckKind;
  ownerPersona: Persona;
  loopTags: readonly string[];
  failureModeIds: readonly string[];
  caseIds: readonly string[];
  done: boolean;
};

export type SafetyInterlock = {
  id: string;
  equipment: string;
  condition: string;
  effect: string;
  priority: 'P1' | 'P2';
  overrideAuthority: OverrideAuthority;
};

export type OverrideChangeRequest = {
  id: string;
  interlockId: string;
  fromAuthority: OverrideAuthority;
  toAuthority: OverrideAuthority;
  reason: string;
  requestedBy: string;
  requestedOn: string;
  status: 'Pending approval';
  approver: string;
};

export type ControlVerificationCheck = {
  id: string;
  interlockId: string;
  /** The short control label the daily meeting's handover reads
   *  (`BM01 motor — HP lube pressure LL`); moved here with the rows from
   *  `meeting/seed.ts` (polish.P20, comp adjudication 5). */
  control: string;
  tier: 'Field spot check' | 'Scheduled audit';
  frequency: string;
  performer: string;
  lastResult: 'pass' | 'degraded' | 'fail';
  onTime: boolean;
  checkedAt: string;
  nextDue: string;
  resultingThreeWId?: string;
};

/** Month-4 ramp Budget rate — 1,440 stpd ÷ 24 h (same basis as the sacred Budget number). */
export const BUDGET_T_H = READINGS.find((row) => row.id === 'rd-kpi-ore-td')!.target! / 24;

export const RISK_ENTRIES: readonly RiskEntry[] = [
  {
    id: 'RK-01',
    label: 'Apron feeder AF01 drive failure',
    circuit: 'Crushing',
    subprocessId: 'sp-crush',
    cause: 'AF01 drive coupling or chain wear',
    effect: 'Feed to crushing stops; fine ore bin draws down',
    safetySeverity: 3,
    productionSeverity: 7,
    severity: 6,
    occurrence: 4,
    detection: 5,
    rpn: 120,
    ownerTeam: 'Maintenance',
    reviewDate: '2026-08-01',
    tripHours: 4,
    bufferHours: 6,
    controlMeasure:
      'Weekly drive coupling and chain inspection; fine ore bin low-level alarm at the 6 h buffer',
    reviewCadence: 'Weekly',
    provenance: '[syn] Crushing FMEA — AF01 drive re-scored two-axis (v19 M6 §5)',
  },
  {
    id: 'RK-02',
    label: 'BM01 trunnion bearing failure',
    circuit: 'Ball Mill',
    subprocessId: 'sp-grind',
    cause: 'Lubrication loss or misalignment on the trunnion bearing',
    effect: 'Unplanned mill stop; grinding throughput lost for the trip duration',
    safetySeverity: 4,
    productionSeverity: 9,
    ...failureModeScore(BM01_FAILURE_MODE_ID),
    ownerTeam: 'Maintenance',
    reviewDate: '2026-07-15',
    tripHours: BM01_BEARING_STOP_HOURS.avoidedFailure,
    hoursSource: 'failure-repair',
    plannedStopHours: BM01_BEARING_STOP_HOURS.plannedInspection,
    bufferHours: 0,
    linkedWorkOrder: 'WO-441',
    controlMeasure:
      'Continuous TE-622-625 bearing temperature trend with Tier 1 TARP; fortnightly vibration route; spare bearing pre-staged',
    reviewCadence: 'Daily',
    failureModeId: BM01_FAILURE_MODE_ID,
    provenance: '[syn] RK-02 score reads fm-trunnion; two-axis safety/production scores remain separate',
  },
  {
    id: 'RK-03',
    label: 'BM01 liner / bolt failure',
    circuit: 'Ball Mill',
    subprocessId: 'sp-grind',
    cause: 'Liner bolt fatigue or a loose segment under cyclic load',
    effect: 'Unplanned mill stop to retorque or replace the segment',
    safetySeverity: 5,
    productionSeverity: 8,
    severity: 7,
    occurrence: 3,
    detection: 5,
    rpn: 105,
    ownerTeam: 'Maintenance',
    reviewDate: '2026-07-22',
    tripHours: 4,
    bufferHours: 0,
    controlMeasure: 'Liner bolt torque check each shutdown; discharge grate inspection on the liner schedule',
    reviewCadence: 'Monthly',
    provenance: '[syn] Ball Mill FMEA — liner/bolts re-scored two-axis (v19 M6 §5)',
  },
  {
    id: 'RK-04',
    label: 'Cyclone feed pump GP01 failure',
    circuit: 'Ball Mill',
    subprocessId: 'sp-grind',
    cause: 'GP01 seal or impeller wear',
    effect: 'Cyclone feed lost until standby GP01S is brought online',
    safetySeverity: 2,
    productionSeverity: 5,
    severity: 5,
    occurrence: 4,
    detection: 3,
    rpn: 60,
    ownerTeam: 'Maintenance',
    reviewDate: '2026-08-12',
    tripHours: 1,
    bufferHours: 0,
    controlMeasure: 'Standby pump auto-changeover tested weekly; seal-water low-flow alarm',
    reviewCadence: 'Weekly',
    provenance: '[syn] Cyclone Cluster FMEA — GP01/GP01S standby swap (v19 M6 §5)',
  },
  {
    id: 'RK-05',
    label: 'Zn rougher CuSO4 activation failure',
    circuit: 'Zn Flotation',
    subprocessId: 'sp-zn',
    cause: 'CuSO4 dosing rate drifts below the activation band',
    effect: 'Low Zn surface activation; Zn rougher recovery declines',
    safetySeverity: 1,
    productionSeverity: 9,
    severity: 10,
    occurrence: 5,
    detection: 3,
    rpn: 150,
    ownerTeam: 'Metallurgy',
    reviewDate: '2026-07-08',
    tripHours: 6,
    bufferHours: 4,
    controlMeasure:
      'CuSO4 dosing FIT low-flow alarm; batch strength checked each shift; lime loop AIC5002 in AUTO',
    reviewCadence: 'Each shift',
    provenance: '[syn] Zn Flotation FMEA — CuSO4 activation, S=10 blended (v19 M6 §2 worked example)',
  },
  {
    id: 'RK-06',
    label: 'Frother dosing failure',
    circuit: 'Zn Flotation',
    subprocessId: 'sp-zn',
    cause: 'Frother (Aero 5100) dosing pump fault or reagent supply interruption',
    effect: 'Froth stability lost; Zn concentrate mass pull falls',
    safetySeverity: 1,
    productionSeverity: 6,
    severity: 6,
    occurrence: 5,
    detection: 3,
    rpn: 90,
    ownerTeam: 'Metallurgy',
    reviewDate: '2026-08-19',
    tripHours: 3,
    bufferHours: 1.8,
    controlMeasure:
      'Frother FIT low-flow alarm; froth depth rodded each shift on the Pb and Zn roughers',
    reviewCadence: 'Each shift',
    provenance: '[syn] Zn Flotation FMEA — Aero 5100 dosing re-scored two-axis (v19 M6 §5)',
  },
  {
    id: 'RK-07',
    label: 'Rougher air supply failure',
    circuit: 'Zn Flotation',
    subprocessId: 'sp-zn',
    cause: 'Blower trim drift or a valve fault moves cell air off design',
    effect: 'Recovery falls across the affected bank',
    safetySeverity: 3,
    productionSeverity: 8,
    severity: 7,
    occurrence: 3,
    detection: 4,
    rpn: 84,
    ownerTeam: 'Control room',
    reviewDate: '2026-07-29',
    tripHours: 5,
    bufferHours: 1.5,
    controlMeasure:
      'Blower duty/standby auto-start tested weekly; air rate checked against design each shift',
    reviewCadence: 'Weekly',
    failureModeId: 'fm-air-flow',
    provenance: '[syn] Zn Flotation FMEA — rougher air supply re-scored two-axis (v19 M6 §5)',
  },
  {
    id: 'RK-08',
    label: 'Pb thickener flocculant failure',
    circuit: 'Thickeners',
    subprocessId: 'sp-filter',
    cause: 'Flocculant dosing pump fault or make-up interruption',
    effect: 'Underflow density falls; thickener overflow clarity degrades',
    safetySeverity: 2,
    productionSeverity: 6,
    severity: 6,
    occurrence: 4,
    detection: 4,
    rpn: 96,
    ownerTeam: 'Control room',
    reviewDate: '2026-08-05',
    tripHours: 4,
    bufferHours: 6,
    controlMeasure: 'Flocculant make-up tank low-level alarm; underflow density checked hourly',
    reviewCadence: 'Daily',
    provenance: '[syn] Thickeners FMEA — Pb thickener flocculant, RPN 96 (v19 M6 §6 worked example)',
  },
  {
    id: 'RK-09',
    label: 'Paste pump GP40 failure',
    circuit: 'Thickeners',
    subprocessId: 'sp-paste',
    cause: 'GP40 seal wear or a discharge line blockage',
    effect: 'Paste discharge halted; thickener underflow backs up',
    safetySeverity: 4,
    productionSeverity: 7,
    severity: 7,
    occurrence: 3,
    detection: 4,
    rpn: 84,
    ownerTeam: 'Maintenance',
    reviewDate: '2026-07-11',
    tripHours: 2.5,
    bufferHours: 1,
    controlMeasure: 'PdM vibration route on GP40; wear parts held at 3-day minimum stock',
    reviewCadence: 'Weekly',
    provenance: '[syn] Thickeners FMEA — GP40 paste pump re-scored two-axis (v19 M6 §5)',
  },
];

/** [syn] shift checklist — six rows; at-risk state is derived in the transform. */
export const SHIFT_CHECKS: readonly ShiftCheck[] = [
  {
    id: 'chk-dcs-walk',
    title: 'Control room parameter walkthrough',
    description:
      'Review every Control Plan tag on the DCS: pH AIC4001 / AIC5002, cell levels, reagent FITs, PSA P80. Flag anything outside its target band.',
    frequency: 'Each shift',
    riskLevel: 'high',
    kind: 'field',
    ownerPersona: 'Control room',
    loopTags: [],
    failureModeIds: ['fm-zn-ph', 'fm-air-flow'],
    caseIds: [],
    done: true,
  },
  {
    id: 'chk-froth-walk',
    title: 'Froth visual inspection — all flotation banks',
    description:
      'Walk the Pb banks and the Zn rougher. Note froth colour, mobility and bubble size. Log any froth-depth discrepancy over 15 mm.',
    frequency: 'Each shift',
    riskLevel: 'medium',
    kind: 'field',
    ownerPersona: 'Operator',
    loopTags: [],
    failureModeIds: ['fm-froth-height'],
    caseIds: [],
    done: true,
  },
  {
    id: 'chk-reagent-levels',
    title: 'Reagent tank level check',
    description:
      'Check CuSO4 and lime tank levels against minimum stock; confirm the next batch is in preparation before hand-over.',
    frequency: 'Each shift',
    riskLevel: 'high',
    kind: 'reagent',
    ownerPersona: 'Metallurgist',
    loopTags: [],
    failureModeIds: ['fm-zn-ph'],
    caseIds: ['case-cuso4'],
    done: false,
  },
  {
    id: 'chk-loop-modes',
    title: 'Control loop mode verification',
    description: 'Confirm every loop is in AUTO or has a logged reason.',
    frequency: 'Each shift',
    riskLevel: 'high',
    kind: 'loop',
    ownerPersona: 'Control room',
    loopTags: ['LIC4010', 'LIC4013', 'WIC1201A', 'AIC5002'],
    failureModeIds: [],
    caseIds: [],
    done: false,
  },
  {
    id: 'chk-tails',
    title: 'Tailings discharge check',
    description:
      'Confirm tailings discharging normally; check the density gauge; no visible concentrate carry-over; log any foam or discolouration.',
    frequency: 'Each shift',
    riskLevel: 'medium',
    kind: 'field',
    ownerPersona: 'Operator',
    loopTags: [],
    failureModeIds: [],
    caseIds: [],
    done: false,
  },
  {
    id: 'chk-rotating-walk',
    title: 'Equipment walk — critical rotating equipment',
    description:
      'BM01 bearing area: listen for noise, check the oil sight glass, confirm TE-622-625 temperature on the DCS. Concentrate pumps: seal drip check.',
    frequency: 'Each shift',
    riskLevel: 'high',
    kind: 'equipment',
    ownerPersona: 'Maintenance',
    loopTags: [],
    failureModeIds: ['fm-trunnion'],
    caseIds: [],
    done: false,
  },
];

/** v19 M6 §4's interlock table, transcribed verbatim, in source order (IL-01…09). */
export const SAFETY_INTERLOCKS: readonly SafetyInterlock[] = [
  {
    id: 'IL-01',
    equipment: 'BM01 Motor',
    condition: 'HP lube pressure LL (PT1103)',
    effect: 'Immediate trip',
    priority: 'P1',
    overrideAuthority: 'No one',
  },
  {
    id: 'IL-02',
    equipment: 'BM01 Motor',
    condition: 'Trunnion bearing temp HH (TT1104)',
    effect: 'Immediate trip',
    priority: 'P1',
    overrideAuthority: 'No one',
  },
  {
    id: 'IL-03',
    equipment: 'BM01 Motor',
    condition: 'Motor power HH > 1,200 kW',
    effect: 'Trip feed CV02',
    priority: 'P1',
    overrideAuthority: 'No one',
  },
  {
    id: 'IL-04',
    equipment: 'BM01 Motor',
    condition: 'Barring drive engaged',
    effect: 'Start blocked',
    priority: 'P1',
    overrideAuthority: 'No one',
  },
  {
    id: 'IL-05',
    equipment: 'GP40 Paste Pump',
    condition: 'Discharge pressure HH > 5,000 kPa',
    effect: 'Immediate trip',
    priority: 'P1',
    overrideAuthority: 'No one',
  },
  {
    id: 'IL-06',
    equipment: 'CV02 Feed',
    condition: 'Fine ore bin LL (<15%)',
    effect: 'Stop CV02',
    priority: 'P2',
    overrideAuthority: 'Supervisor only',
  },
  {
    id: 'IL-07',
    equipment: 'AF01 Apron Feeder',
    condition: 'FOB bin level HH (80%)',
    effect: 'Stop AF01 + CR01',
    priority: 'P2',
    overrideAuthority: 'Supervisor only',
  },
  {
    id: 'IL-08',
    equipment: 'TK01/TK02 Rake',
    condition: 'Rake torque HH (85%)',
    effect: 'Auto-lift rake, reduce speed',
    priority: 'P1',
    overrideAuthority: 'Maintenance mode',
  },
  {
    id: 'IL-09',
    equipment: 'Paste Pump GP40',
    condition: 'Suction pressure LL (<50 kPa)',
    effect: 'Alarm, reduce speed',
    priority: 'P2',
    overrideAuthority: 'Operator',
  },
];

export const OVERRIDE_CHANGE_REQUESTS: readonly OverrideChangeRequest[] = [
  {
    id: 'OCR-01',
    interlockId: 'IL-03',
    fromAuthority: 'No one',
    toAuthority: 'Maintenance mode',
    reason: 'Planned commissioning overload trial on the BM01 motor, run under controlled maintenance supervision',
    requestedBy: 'Maintenance Superintendent',
    requestedOn: '2026-06-05',
    status: 'Pending approval',
    approver: 'Plant Manager',
  },
];

/** The on-time bar every control-verification figure is read against — Risk's
 *  band and the daily meeting's header (moved from `meeting/`, comp
 *  adjudication 5). A seeded assumption, not a site standard. */
export const CONTROL_VERIFICATION_TARGET_PCT = 95;

/** The one CV register (CV-01…09, one per interlock). The daily meeting reads
 *  these rows through `meeting/seed.ts`; Risk owns them. */
export const CONTROL_VERIFICATION_CHECKS: readonly ControlVerificationCheck[] = [
  {
    id: 'CV-01',
    interlockId: 'IL-01',
    control: 'BM01 motor — HP lube pressure LL',
    tier: 'Field spot check',
    frequency: 'Shift-based',
    performer: 'Field operator',
    lastResult: 'pass',
    onTime: true,
    checkedAt: '2026-06-08 day shift',
    nextDue: '2026-06-08 night shift',
  },
  {
    id: 'CV-02',
    interlockId: 'IL-02',
    control: 'BM01 motor — trunnion bearing temp HH',
    tier: 'Scheduled audit',
    frequency: 'Monthly',
    performer: 'Senior reliability engineer',
    lastResult: 'pass',
    onTime: true,
    checkedAt: '2026-06-01',
    nextDue: '2026-07-01',
  },
  {
    id: 'CV-03',
    interlockId: 'IL-03',
    control: 'BM01 motor — motor power HH',
    tier: 'Field spot check',
    frequency: 'Shift-based',
    performer: 'Field operator',
    lastResult: 'pass',
    onTime: true,
    checkedAt: '2026-06-08 day shift',
    nextDue: '2026-06-08 night shift',
  },
  {
    id: 'CV-04',
    interlockId: 'IL-04',
    control: 'BM01 motor — barring drive engaged',
    tier: 'Field spot check',
    frequency: 'Shift-based',
    performer: 'Field operator',
    lastResult: 'pass',
    onTime: true,
    checkedAt: '2026-06-08 day shift',
    nextDue: '2026-06-08 night shift',
  },
  {
    id: 'CV-05',
    interlockId: 'IL-05',
    control: 'GP40 paste pump — discharge pressure HH',
    tier: 'Scheduled audit',
    frequency: 'Quarterly',
    performer: 'Safety officer',
    lastResult: 'pass',
    onTime: true,
    checkedAt: '2026-06-02',
    nextDue: '2026-09-01',
  },
  {
    id: 'CV-06',
    interlockId: 'IL-06',
    control: 'CV02 feed — fine ore bin LL',
    tier: 'Field spot check',
    frequency: 'Shift-based',
    performer: 'Operations supervisor',
    lastResult: 'pass',
    onTime: true,
    checkedAt: '2026-06-08 day shift',
    nextDue: '2026-06-08 night shift',
  },
  {
    id: 'CV-07',
    interlockId: 'IL-07',
    control: 'AF01 apron feeder — FOB bin level HH',
    tier: 'Field spot check',
    frequency: 'Shift-based',
    performer: 'Operations supervisor',
    lastResult: 'pass',
    onTime: false,
    checkedAt: '2026-06-07 night shift',
    nextDue: '2026-06-08 day shift',
  },
  {
    id: 'CV-08',
    interlockId: 'IL-08',
    control: 'TK01/TK02 rake — rake torque HH',
    tier: 'Field spot check',
    frequency: 'Shift-based',
    performer: 'Maintenance Superintendent',
    lastResult: 'degraded',
    onTime: true,
    checkedAt: '2026-06-07 night shift',
    nextDue: '2026-06-08 day shift',
    resultingThreeWId: '3W-118',
  },
  {
    id: 'CV-09',
    interlockId: 'IL-09',
    control: 'GP40 paste pump — suction pressure LL',
    tier: 'Field spot check',
    frequency: 'Shift-based',
    performer: 'Field operator',
    lastResult: 'pass',
    onTime: true,
    checkedAt: '2026-06-08 day shift',
    nextDue: '2026-06-08 night shift',
  },
];
